import React, { useEffect, useState } from 'react'
import { Form } from '@s4l/ds'
import { IconTrash } from '@s4l/icons/IconTrash'
import { useS4LContext } from '@s4l/react-context'
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import PriceHistogram from '../../PriceHistogram'
import styles from './index.module.css'
import { ListOfPriceDistribution } from '@s4l/stayforlong/property/ListOfPriceDistribution'
import { PriceDistribution } from '@s4l/stayforlong/property/PriceDistribution'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'
import { Money } from '@s4l/stayforlong/kernel/Money'

interface IFilterPricingProps {
  defaultMinPrice?: number
  defaultMaxPrice?: number
  listOfPriceDistributionJSON: Parameters<(typeof ListOfPriceDistribution)['fromJSON']>[0]
}

const FilterPricing = ({
  listOfPriceDistributionJSON,
  defaultMinPrice,
  defaultMaxPrice,
}: IFilterPricingProps) => {
  const { i18n } = useS4LContext()

  const [applyDefaultValues, setApplyDefaultValues] = useState(false)
  const { data: priceType, loading: isLoadingPriceType } = useUseCase('GetPriceTypeSharedUseCase')
  const { data: currency, loading: isLoadingCurrency } = useUseCase('GetCurrencySharedUseCase')
  const { data: market, loading: isLoadingMarket } = useUseCase('GetCurrentMarketSharedUseCase')

  let listOfPriceDistributionModel = ListOfPriceDistribution.fromJSON(listOfPriceDistributionJSON)

  const isEmpty = listOfPriceDistributionModel.isEmpty()

  const min = listOfPriceDistributionModel.getMinValue()
  const max = listOfPriceDistributionModel.getMaxValue()

  const [selectedRange, setSelectedRange] = useState<number[]>([])

  const [minValue, setMinValue] = useState<number | string>(defaultMinPrice ?? min)
  const [maxValue, setMaxValue] = useState<number | string>(defaultMaxPrice ?? '')

  const handleChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      setSelectedRange(value)

      const minPrice =
        listOfPriceDistributionModel
          .getList()
          .at(value[0] - 1)
          ?.getValue() ?? min
      const maxPrice =
        listOfPriceDistributionModel
          .getList()
          .at(value[1] - 1)
          ?.getValue() ?? ''
      setMinValue(minPrice)
      setMaxValue(maxPrice)
    }
  }

  const handleInputChange = (name: 'min' | 'max', value: number | string) => {
    const numValue = Number(value || Number.MAX_SAFE_INTEGER)

    let listOfPriceDistributionModelList = listOfPriceDistributionModel.getList()
    listOfPriceDistributionModelList = [
      ...listOfPriceDistributionModelList,
      PriceDistribution.create({ value: Number.MAX_SAFE_INTEGER, count: 0 }),
    ]

    let closestIndex = listOfPriceDistributionModelList.reduce((prevIndex, curr, currIndex) => {
      const prevValue = listOfPriceDistributionModel.getList()[prevIndex].getValue()
      return Math.abs(curr.getValue() - numValue) < Math.abs(prevValue - numValue)
        ? currIndex
        : prevIndex
    }, 0)

    if (value === max) {
      closestIndex += 1
    }

    closestIndex += 1

    setSelectedRange(prev => {
      if (name === 'min') {
        return [closestIndex, prev[1]]
      }

      return [prev[0], closestIndex]
    })
    if (name === 'min') {
      setMinValue(value)
    } else {
      setMaxValue(value)
    }
  }

  useEffect(() => {
    if (!isEmpty) {
      setSelectedRange([1, listOfPriceDistributionModel.getList().length + 1])
      setMinValue(defaultMinPrice ?? min)
      setMaxValue(defaultMaxPrice ?? '')
      handleInputChange('min', defaultMinPrice ?? min)
      handleInputChange('max', defaultMaxPrice ?? '')
      setApplyDefaultValues(true)
    } else {
      setMinValue(0)
      setMaxValue(0)
    }
  }, [isEmpty]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (listOfPriceDistributionModel.getList().length === 0 || !applyDefaultValues) return

    const newSelectedRange = [...selectedRange]
    const newMinValue = listOfPriceDistributionModel.getList()[newSelectedRange[0] - 1]
    const newMaxValue = listOfPriceDistributionModel.getList()[newSelectedRange[1] - 1]

    if (!newSelectedRange[0] || !newMinValue || newMinValue.getValue() > Number(minValue)) {
      newSelectedRange[0] = 1

      setMinValue(listOfPriceDistributionModel.getList()[0].getValue())
    }

    if (!newSelectedRange[1] || !newMaxValue || !maxValue) {
      newSelectedRange[1] = listOfPriceDistributionModel.getList().length + 1
      setMaxValue('')
    }

    setSelectedRange(newSelectedRange)
  }, [listOfPriceDistributionModel.serialized]) // eslint-disable-line react-hooks/exhaustive-deps

  listOfPriceDistributionModel = listOfPriceDistributionModel.setSelectedRange([
    listOfPriceDistributionModel
      .getList()
      .at(selectedRange[0] - 1)
      ?.getValue() ?? min,
    listOfPriceDistributionModel
      .getList()
      .at(selectedRange[1] - 1)
      ?.getValue() ?? max + 1,
  ])

  const titleMessageKey = priceType?.isFeaturedPriceTotal()
    ? 'global.total-price'
    : 'global.price-per-night'

  const currencySymbol = Money.fromJSON({
    amount: 0,
    currency: currency?.value ?? 'USD',
  })?.getSymbol(i18n.locale, market)

  const hasLoaded = !isLoadingPriceType && !isLoadingCurrency && !isLoadingMarket
  return (
    <Form.Fieldset data-testid={hasLoaded ? 'loaded' : 'not-loaded'}>
      <Form.Legend>
        {i18n.formatMessage({ id: titleMessageKey })} ({currencySymbol})
      </Form.Legend>
      <Form.ClearButton icon={<IconTrash />}>
        {i18n.formatMessage({ id: 'serp.filter-clear' })}
      </Form.ClearButton>

      <div className={styles.gridContainer}>
        <PriceHistogram
          className={['col-start-2', 'col-span-2']}
          listOfPriceDistributionJSON={listOfPriceDistributionModel.toJSON()}
        />

        <Form.Skeleton isLoading={isEmpty}>
          <Form.Slider
            name={['minIndex', 'maxIndex']}
            className={['col-start-1', 'col-span-4']}
            min={1}
            max={listOfPriceDistributionModel.getList().length + 1}
            step={1}
            value={selectedRange}
            onChange={handleChange}
          />
        </Form.Skeleton>

        <Form.Label className={['mt-32', 'desktop:mt-24', 'col-span-2']}>
          {i18n.formatMessage({ id: 'global.minimum' })}
          <Form.Input
            name="minPrice"
            min={min}
            max={max}
            falseValue={min}
            value={minValue}
            type="tel"
            onBlur={value => handleInputChange('min', value)}
            onClear={() => handleInputChange('min', min)}
            onChange={setMinValue}
            disabled={isEmpty}
          />
        </Form.Label>
        <Form.Label className={['mt-32', 'desktop:mt-24', 'col-span-2']}>
          {i18n.formatMessage({ id: 'global.maximum' })}
          <Form.Input
            name="maxPrice"
            min={min}
            max={max}
            falseValue=""
            value={maxValue}
            type="tel"
            onBlur={value => handleInputChange('max', value)}
            onClear={() => handleInputChange('max', '')}
            displayValue={value => {
              if (!value) {
                return `+${max}`
              }
              return value
            }}
            parseValue={value => {
              if (!value) {
                return max
              }
              return value
            }}
            onChange={setMaxValue}
            disabled={isEmpty}
          />
        </Form.Label>
      </div>
    </Form.Fieldset>
  )
}

FilterPricing.displayName = 'SearchFilters.FilterPricing'
export default withErrorBoundary(FilterPricing)
